import React from "react";
import { Info } from "lucide-react";
import type { ReactElement } from "react";
import { Popover, PopoverContent, PopoverTrigger, cn } from "@ndd/ui";
import type { CompactMetricEnvelope } from "../../../../shared/metrics";
import {
  barGeometry,
  chipTokens,
  fleetRowText,
  formatValue,
  targetRowText,
  tileSentence,
  unitWord,
  type BarGeometry,
  type BarZone,
  type HeadlineMetricConfig,
} from "../../lib/snapshot-display";
import {
  MISSING_MINIMUM_FLAG,
  MISSING_MINIMUM_LINK_LABEL,
  showsMissingMinimumFlag,
  type FunctionalFormLink,
} from "../../lib/metrics-display";
import { MetricDefinition } from "./metric-definition";
import {
  THEMED_CARD,
  THEMED_CARD_TITLE,
  THEMED_DISPLAY_VALUE,
} from "../../lib/always-look";

/*
 * Every tile is a column of fixed-height tracks (header, value row, two-line
 * sentence reserve, bar, two single-line rows) so the sections line up across
 * a row of the 2x3 grid, whatever each tile's text length.
 */
export const TILE_CLASS = cn(
  "flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm",
  THEMED_CARD
);
export const TILE_HEADER_CLASS = "flex h-6 items-center justify-between gap-2";
export const TILE_VALUE_ROW_CLASS = "flex h-[37px] items-baseline gap-2";
export const TILE_SENTENCE_CLASS =
  "line-clamp-2 min-h-[38px] text-body leading-[1.45] text-foreground";
// Under the always theme the tile's title is in display type (NO-792).
const TITLE_CLASS = cn(
  "font-sans text-caption font-extrabold uppercase tracking-[0.08em] text-muted-strong",
  THEMED_CARD_TITLE
);
const ROW_VALUE_CLASS = cn("truncate text-right text-foreground", THEMED_DISPLAY_VALUE);
const ROW_CLASS =
  "flex h-4 items-baseline justify-between gap-3 whitespace-nowrap text-xs";
const MARKER_CLASS = "absolute -top-[3px] h-3";

function pct(value: number): string {
  return `${value}%`;
}

function ZoneBand({
  zone,
  name,
  className,
}: {
  zone: BarZone | null;
  name: string;
  className: string;
}): ReactElement | null {
  if (!zone) return null;

  return (
    <div
      className={cn("absolute inset-y-0", className)}
      data-zone={name}
      style={{ left: pct(zone.startPct), width: pct(zone.endPct - zone.startPct) }}
    />
  );
}

function BarMarkers({ geometry }: { geometry: BarGeometry }): ReactElement {
  return (
    <>
      {geometry.ticksPct.map((tick, index) => (
        <div
          key={`tick-${index}`}
          className={cn(MARKER_CLASS, "w-px bg-foreground brand:w-0.5")}
          data-zone="tick"
          style={{ left: pct(tick) }}
        />
      ))}
      {geometry.fleetPct !== null && (
        <div
          className={cn(MARKER_CLASS, "w-0 border-l border-dotted border-sand-500")}
          data-zone="fleet"
          style={{ left: pct(geometry.fleetPct) }}
        />
      )}
    </>
  );
}

/**
 * The zoned goal bar: mint good zone, amber watch band, ticks, chip-colored
 * fill, dotted fleet marker. Under the always theme (D5) it is a track with a
 * 4px value stroke, a muted olive target band, no watch band and a 2px tick.
 */
export function MetricGoalBar({
  metric,
}: {
  metric: CompactMetricEnvelope;
  config?: HeadlineMetricConfig;
}): ReactElement {
  const geometry = barGeometry(metric);
  const tokens = chipTokens(metric.chip);

  return (
    <div
      className="relative h-[6px] w-full rounded-full bg-muted brand:bg-secondary"
      role="img"
      aria-label={`${targetRowText(metric)}; all RDs ${fleetRowText(metric)}`}
      data-testid="goal-bar"
    >
      <ZoneBand
        zone={geometry.good}
        name="good"
        className="rounded-full bg-success-soft brand:bg-primary/16"
      />
      <ZoneBand
        zone={geometry.watch}
        name="watch"
        className="bg-warning-soft brand:bg-transparent"
      />
      {geometry.fillPct > 0 && (
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full brand:inset-y-px",
            tokens.fill
          )}
          data-zone="fill"
          style={{ width: pct(geometry.fillPct) }}
        />
      )}
      <BarMarkers geometry={geometry} />
    </div>
  );
}

function TileHeader({
  config,
  metric,
}: {
  config: HeadlineMetricConfig;
  metric: CompactMetricEnvelope;
}): ReactElement {
  const tokens = chipTokens(metric.chip);

  return (
    <div className={TILE_HEADER_CLASS}>
      <div className="flex items-center gap-1.5">
        <h3 className={TITLE_CLASS}>{config.title}</h3>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`About ${config.title}`}
              className="flex h-4 w-4 cursor-pointer items-center justify-center rounded-full text-subtle hover:text-foreground"
            >
              <Info className="h-[14px] w-[14px]" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent variant="warm" align="start" className="w-auto p-3">
            <MetricDefinition title={config.title} metric={metric} />
          </PopoverContent>
        </Popover>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-(--highlight-radius,calc(infinity*1px)) px-(--highlight-px,0.625rem) py-(--highlight-py,3px) font-(family-name:--highlight-font) text-label font-(weight:--highlight-weight,600) leading-none",
          tokens.pill
        )}
        data-testid="metric-chip"
      >
        {metric.chip}
      </span>
    </div>
  );
}

function TileRows({ metric }: { metric: CompactMetricEnvelope }): ReactElement {
  const target = targetRowText(metric);
  const allRds = fleetRowText(metric);

  return (
    <dl className="flex flex-col gap-1">
      <div className={ROW_CLASS}>
        <dt className="text-muted-foreground">Target</dt>
        <dd className={ROW_VALUE_CLASS} title={target} data-testid="metric-target">
          {target}
        </dd>
      </div>
      <div className={ROW_CLASS}>
        <dt className="text-muted-foreground">All RDs</dt>
        <dd className={ROW_VALUE_CLASS} title={allRds} data-testid="metric-fleet">
          {allRds}
        </dd>
      </div>
    </dl>
  );
}

/** One headline tile: title + info popover, chip, value, sentence, goal bar, Target / All RDs rows. */
export function MetricTile({
  config,
  metric,
  functionalFormLink,
}: {
  config: HeadlineMetricConfig;
  metric: CompactMetricEnvelope;
  /** Wraps the missing-minimum flag's link; the route supplies it. */
  functionalFormLink?: FunctionalFormLink;
}): ReactElement {
  const unit = unitWord(metric, config);

  return (
    <section className={TILE_CLASS} aria-label={config.title} data-testid="metric-tile">
      <TileHeader config={config} metric={metric} />

      <p className={TILE_VALUE_ROW_CLASS}>
        <span
          className="font-sans text-[34px] font-bold leading-none text-foreground"
          data-testid="metric-value"
        >
          {formatValue(metric)}
        </span>
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </p>

      <p className={TILE_SENTENCE_CLASS} data-testid="metric-sentence">
        {tileSentence(metric)}
      </p>

      {showsMissingMinimumFlag(metric) && (
        <p className="text-xs text-muted-strong" data-testid="metric-minimum-flag">
          {MISSING_MINIMUM_FLAG}
          {functionalFormLink
            ? functionalFormLink(MISSING_MINIMUM_LINK_LABEL)
            : MISSING_MINIMUM_LINK_LABEL}
        </p>
      )}

      <div className="pt-1">
        <MetricGoalBar metric={metric} config={config} />
      </div>

      <TileRows metric={metric} />
    </section>
  );
}
